import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ImageCroppedEvent,
  ImageCropperComponent,
  ImageTransform,
  ImageCropperModule,
} from 'ngx-image-cropper';
import { Capacitor } from '@capacitor/core';
import { AuthService } from '../../../core/services/auth/auth.service';
import { AlertService } from '../../../core/services/navigation/alert.service';
import { LoggerService } from '../../../core/services/logger/logger.service';
import { downscaleImageFile, toJpg } from '../../../core/utilities/image-resize.util';
import { addIcons } from 'ionicons';
import {
  addCircle,
  refresh,
  codeOutline,
  chevronExpandOutline,
  closeCircleOutline,
  checkmarkCircleOutline,
} from 'ionicons/icons';
import {
  IonIcon,
  IonModal,
  IonRow,
  IonCol,
  IonButton,
  IonPopover,
  IonImg,
  IonSkeletonText,
} from '@ionic/angular/standalone';
import { User } from 'src/app/core/models/User';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-avatar',
  templateUrl: './avatar.component.html',
  styleUrls: ['./avatar.component.scss'],
  standalone: true,
  imports: [ImageCropperModule, IonIcon, IonModal, IonRow, IonCol, IonButton, IonPopover, IonImg, IonSkeletonText, CommonModule],
})
export class AvatarComponent implements OnInit {
  @ViewChild('cropper') cropper!: ImageCropperComponent;
  @Input() set avatarUrl(url: string | null) {
    if (url) {
      this.downloadImage();
    }
  }
  @Output() upload = new EventEmitter<string>();

  _avatarUrl!: string;
  imageFile?: File;
  transform: ImageTransform = {};
  maintainAspectRatio: boolean = true;
  isMobile = Capacitor.getPlatform() !== 'web';
  isModalOpen = false;
  loggedInUser!: User;
  isImageLoaded: boolean = false;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly authService: AuthService,
    private alertService: AlertService,
    private readonly logger: LoggerService,
  ) {
    addIcons({
      addCircle,
      refresh,
      codeOutline,
      chevronExpandOutline,
      closeCircleOutline,
      checkmarkCircleOutline,
    });
  }

  ngOnInit(): void {
    this.authService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      if (user) {
        this.loggedInUser = user;
        if (user.avatarUrl === 'test' || !user.avatarUrl) {
          this.loggedInUser.avatarUrl = 'assets/ic-profile-avatar.svg';
        }
      }
    });
  }

  downloadImage() {
    // Download image from server
  }

  async selectImage(e: Event): Promise<void> {
    const input: HTMLInputElement = e.target as HTMLInputElement;
    const file: File | undefined = input.files?.[0];
    if (!file) {
      return;
    }
    this.maintainAspectRatio = true;
    try {
      this.imageFile = await downscaleImageFile(file);
      this.setOpen(true);
    } catch (error: unknown) {
      this.logger.error('Failed to prepare selected image', error, { fileSize: file.size });
      this.alertService.showAlertMessage('Upload failed', 'We could not open that image. Please try a different photo.');
    } finally {
      input.value = '';
    }
  }

  setOpen(isOpen: boolean) {
    this.isModalOpen = isOpen;
  }

  imageCropped(event: ImageCroppedEvent) {
    this._avatarUrl = URL.createObjectURL(event.blob!);
    this.uploadImage();
  }

  imageLoaded(): void {
    setTimeout(() => {
      this.maintainAspectRatio = false;
    }, 500);
  }

  loadImageFailed() {
    this.setOpen(false);
    this.logger.error('Cropper failed to load profile image');
    this.alertService.showAlertMessage('Upload failed', 'We could not open that image. Please try a different photo.');
  }

  cropImage() {
    this.cropper.crop();
    this.setOpen(false);
  }

  discardChanges() {
    this.setOpen(false);
  }

  // Image edit functions
  rotate() {
    const newValue: number = ((this.transform.rotate ?? 0) + 90) % 360;

    this.transform = {
      ...this.transform,
      rotate: newValue,
    };
  }

  flipHorizontal() {
    this.transform = {
      ...this.transform,
      flipH: !this.transform.flipH,
    };
  }

  flipVertical() {
    this.transform = {
      ...this.transform,
      flipV: !this.transform.flipV,
    };
  }

  uploadImage(): void {
    const id: number = Number(this.loggedInUser.id);
    if (!this._avatarUrl) {
      this.logger.error('Invalid cropped image URL');
      return;
    }

    toJpg(this._avatarUrl, { name: 'avatar.jpg' }).then((file: File) => {
      URL.revokeObjectURL(this._avatarUrl);
      this.authService.changeProfilePic(id, file).subscribe({
        next: () => {
          this.authService.refreshCurrentUser();
        },
        error: (error: unknown) => {
          this.logger.error('Failed to upload profile image', error, { userId: id });
          this.alertService.showAlertMessage('Upload failed', 'We could not upload your photo. Please try again.');
        },
      });
    }).catch((error: unknown) => {
      this.logger.error('Failed to convert profile image', error);
      this.alertService.showAlertMessage('Upload failed', 'We could not process that image. Please try a different photo.');
    });
  }
}
